import React, { useContext, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import AuthContext from "../context/authContext";
import { AUTH_STATUS } from "../context/enums/authEnums";
import { RootState } from "../store/store";
import { isAuthenticatedAppRoute } from "../Utils/authenticatedAppRoute";
import { useGetCompanyBasicInfo } from "../hooks/api/useGetCompanyBasicInfo";
import { setCompanyCurrencyCode } from "../store/slices/companySlice";

/** Loads tenant currency into `company` slice from `getCompanyBasicInfo`. */
const CompanyBasicInfoSync: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { authContextInfo } = useContext(AuthContext);
  const companyId = useSelector(
    (state: RootState) => state.user.userDetails?.companyId
  );
  const currencyCode = useSelector(
    (state: RootState) => state.company.currencyCode
  );
  const getCompanyBasicInfo = useGetCompanyBasicInfo();
  const mutateAsyncRef = useRef(getCompanyBasicInfo.mutateAsync);
  mutateAsyncRef.current = getCompanyBasicInfo.mutateAsync;
  const resetRef = useRef(getCompanyBasicInfo.reset);
  resetRef.current = getCompanyBasicInfo.reset;
  const syncedCompanyIdRef = useRef<string | null>(null);

  const loginSucceeded =
    authContextInfo.auth.status === AUTH_STATUS.AUTHENTICATED &&
    authContextInfo.auth.authorized === true;
  const canSync = loginSucceeded && isAuthenticatedAppRoute(location.pathname);

  useEffect(() => {
    if (!canSync) {
      syncedCompanyIdRef.current = null;
      resetRef.current();
      if (currencyCode != null) {
        dispatch(setCompanyCurrencyCode(null));
      }
      return;
    }

    if (!companyId || syncedCompanyIdRef.current === companyId) {
      return;
    }

    syncedCompanyIdRef.current = companyId;
    void mutateAsyncRef.current().catch(() => {
      /* alerts via mutation + fetchGraphQL */
    });
  }, [canSync, companyId, dispatch]);

  return null;
};

export default CompanyBasicInfoSync;
